import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  Package,
  Search,
  Ship,
  Factory,
  Globe,
  Hash,
  ChevronRight,
  X,
  ExternalLink,
  RefreshCw,
  TrendingUp,
  Users,
  Filter,
  ArrowUpDown
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";

const ExportProducts = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(50);
  
  // Factory modal state
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productFactories, setProductFactories] = useState([]);
  const [loadingFactories, setLoadingFactories] = useState(false);
  const [showFactoryModal, setShowFactoryModal] = useState(false);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setCurrentPage(0); // Reset to first page on search
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch products
  useEffect(() => {
    fetchProducts();
  }, [debouncedSearch, currentPage]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = {
        limit: pageSize,
        skip: currentPage * pageSize
      };
      if (debouncedSearch) {
        params.search = debouncedSearch;
      }
      
      const response = await axios.get(`${API}/export/products`, { params });
      setProducts(response.data.products || []);
      setTotalCount(response.data.total || 0);
    } catch (error) {
      toast.error("Failed to load products");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProductFactories = async (productName) => {
    setLoadingFactories(true);
    try {
      const encodedProduct = encodeURIComponent(productName);
      const response = await axios.get(`${API}/export/products/${encodedProduct}/factories`);
      setProductFactories(response.data.factories || []);
      setSelectedProduct(productName);
      setShowFactoryModal(true);
    } catch (error) {
      toast.error("Failed to load factory details");
      console.error(error);
    } finally {
      setLoadingFactories(false);
    }
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  // Summary stats
  const totalShipments = products.reduce((sum, p) => sum + p.shipment_count, 0);
  const totalFactories = products.reduce((sum, p) => sum + p.factory_count, 0);

  return (
    <div className="space-y-6" data-testid="export-products-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Products</h1>
          <p className="text-slate-500 text-sm">
            Search and analyze products across all shipments
          </p>
        </div>
        <Button variant="outline" onClick={fetchProducts} className="gap-2" data-testid="refresh-products-btn">
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      {/* Search Bar */}
      <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
        <CardContent className="py-4">
          <div className="flex flex-col sm:flex-row gap-4 items-center">
            <div className="flex items-center gap-2 text-green-700">
              <Search className="w-5 h-5" />
              <span className="font-semibold">Search Products</span>
            </div>
            <div className="flex-1 w-full">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by product name, HSN code, or factory name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-white"
                  data-testid="product-search-input"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
            {debouncedSearch && (
              <Badge variant="secondary" className="bg-green-100 text-green-700">
                {totalCount} results
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Package className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Products</p>
                <p className="text-2xl font-bold">{totalCount.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Ship className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Shipments (visible)</p>
                <p className="text-2xl font-bold">{totalShipments.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Factory className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Factories (visible)</p>
                <p className="text-2xl font-bold">{totalFactories.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Filter className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Page</p>
                <p className="text-2xl font-bold">{currentPage + 1} / {totalPages || 1}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Products Table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Package className="w-5 h-5 text-green-600" />
            Product Catalog
            {debouncedSearch && (
              <Badge variant="outline" className="ml-2 bg-green-50">
                Filtered by: "{debouncedSearch}"
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-green-500" />
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No products found</p>
              {debouncedSearch && (
                <p className="text-sm mt-1">Try a different search term</p>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="w-[40%]">Product Description</TableHead>
                    <TableHead className="text-center">Shipments</TableHead>
                    <TableHead className="text-center">Total Qty</TableHead>
                    <TableHead className="text-center">Factories</TableHead>
                    <TableHead>HSN Codes</TableHead>
                    <TableHead>Import Countries</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {products.map((product, idx) => (
                    <TableRow 
                      key={idx} 
                      className="hover:bg-slate-50 cursor-pointer"
                      onClick={() => fetchProductFactories(product.product)}
                      data-testid={`product-row-${idx}`}
                    >
                      <TableCell className="font-medium">
                        <div className="max-w-md">
                          <p className="line-clamp-2 text-slate-900">{product.product}</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                          {product.shipment_count}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="font-medium">
                          {product.total_quantity?.toLocaleString() || 0}
                        </span>
                        {product.primary_unit && (
                          <span className="text-xs text-slate-500 ml-1">{product.primary_unit}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge 
                          variant="outline" 
                          className="bg-purple-50 text-purple-700 border-purple-200 cursor-pointer hover:bg-purple-100"
                        >
                          <Factory className="w-3 h-3 mr-1" />
                          {product.factory_count}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {product.hs_codes?.slice(0, 2).map((code, i) => (
                            <span key={i} className="text-xs font-mono bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                              {code}
                            </span>
                          ))}
                          {product.hs_code_count > 2 && (
                            <span className="text-xs text-slate-400">+{product.hs_code_count - 2}</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {product.import_countries?.slice(0, 2).map((country, i) => (
                            <Badge key={i} variant="outline" className="text-xs">
                              {country}
                            </Badge>
                          ))}
                          {product.import_countries?.length > 2 && (
                            <span className="text-xs text-slate-400">+{product.import_countries.length - 2}</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="gap-1"
                          onClick={(e) => {
                            e.stopPropagation();
                            fetchProductFactories(product.product);
                          }}
                        >
                          View
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
        
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t">
            <p className="text-sm text-slate-500">
              Showing {currentPage * pageSize + 1} - {Math.min((currentPage + 1) * pageSize, totalCount)} of {totalCount}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage === 0}
                onClick={() => setCurrentPage(p => p - 1)}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage >= totalPages - 1}
                onClick={() => setCurrentPage(p => p + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Factory Modal */}
      <Dialog open={showFactoryModal} onOpenChange={setShowFactoryModal}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Factory className="w-5 h-5 text-purple-600" />
              Factories Importing This Product
            </DialogTitle>
            <p className="text-sm text-slate-500 mt-1 line-clamp-2">{selectedProduct}</p>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto mt-4">
            {loadingFactories ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin text-purple-500" />
              </div>
            ) : productFactories.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Factory className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No factories found</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Factory Name</TableHead>
                    <TableHead>Country</TableHead>
                    <TableHead className="text-center">Shipments</TableHead>
                    <TableHead className="text-center">Quantity</TableHead>
                    <TableHead className="text-center">Score</TableHead>
                    <TableHead>Stage</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {productFactories.map((factory, idx) => (
                    <TableRow key={idx} className="hover:bg-slate-50">
                      <TableCell className="font-medium">
                        {factory.factory_name}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          <Globe className="w-3 h-3 mr-1" />
                          {factory.country || 'N/A'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                          {factory.shipment_count}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="font-medium">
                          {factory.total_quantity?.toLocaleString() || 0}
                        </span>
                        {factory.primary_unit && (
                          <span className="text-xs text-slate-500 ml-1">{factory.primary_unit}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {factory.target_score != null ? (
                          <Badge className="bg-yellow-100 text-yellow-800">
                            {factory.target_score}
                          </Badge>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {factory.pipeline_stage ? (
                          <Badge variant="outline" className="capitalize text-xs">
                            {factory.pipeline_stage.replace(/_/g, ' ')}
                          </Badge>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {factory.factory_id ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="gap-1 text-blue-600"
                            onClick={() => {
                              setShowFactoryModal(false);
                              navigate(`/export/factories/${factory.factory_id}`);
                            }}
                          >
                            View
                            <ExternalLink className="w-3 h-3" />
                          </Button>
                        ) : (
                          <span className="text-xs text-slate-400">Not in DB</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
          
          {/* Modal Footer */}
          <div className="flex items-center justify-between pt-4 border-t mt-4">
            <p className="text-sm text-slate-500">
              {productFactories.length} factories found
            </p>
            <Button variant="outline" onClick={() => setShowFactoryModal(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExportProducts;
